import { useEffect, useRef, useState } from "react";
import {
  Download,
  FileSpreadsheet,
  Loader2,
  Plus,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { api, API_BASE, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "outline",
  called: "default",
  failed: "destructive",
  do_not_call: "secondary",
};

function formatPhone(e164) {
  if (!e164) return "—";
  const p = parsePhoneNumberFromString(e164);
  return p ? p.formatInternational() : e164;
}

export default function Contacts() {
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [confirmDeleteList, setConfirmDeleteList] = useState(null);

  const fetchLists = async () => {
    try {
      const res = await api.get("/contact-lists");
      setLists(res.data);
      if (!selectedId && res.data.length > 0) setSelectedId(res.data[0].id);
    } catch (err) {
      toastError(err, "Failed to load contact lists");
    }
  };

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = lists.find((l) => l.id === selectedId);

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contact Lists</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload CSV / XLS / XLSX. Phones validated to E.164. Custom variables (any column starting with{" "}
            <code className="text-xs">custom_var_</code>) flow through to the agent prompt.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="contacts-template-csv">
            <a href={`${API_BASE}/contact-lists/template.csv`}>
              <Download className="h-4 w-4" /> CSV template
            </a>
          </Button>
          <Button variant="outline" asChild data-testid="contacts-template-xlsx">
            <a href={`${API_BASE}/contact-lists/template.xlsx`}>
              <Download className="h-4 w-4" /> XLSX template
            </a>
          </Button>
          <Button onClick={() => setCreatingOpen(true)} data-testid="contacts-new-list">
            <Plus className="h-4 w-4" /> New list
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left pane: lists */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3 space-y-2" data-testid="contacts-sidebar">
          {lists.length === 0 ? (
            <div className="rounded-lg border border-border bg-card p-6 text-center" data-testid="contacts-empty-lists">
              <Users className="mx-auto h-6 w-6 text-muted-foreground" />
              <p className="mt-2 text-sm">No lists yet</p>
              <Button onClick={() => setCreatingOpen(true)} size="sm" className="mt-3">
                <Plus className="h-4 w-4" /> Create your first list
              </Button>
            </div>
          ) : (
            lists.map((l) => (
              <button
                key={l.id}
                onClick={() => setSelectedId(l.id)}
                data-testid={`contacts-list-card-${l.id}`}
                className={cn(
                  "block w-full rounded-md border p-3 text-left transition",
                  l.id === selectedId
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border bg-card hover:border-primary/40"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{l.name}</span>
                  <Badge variant="outline" className="text-[10px]">{l.valid_rows} valid</Badge>
                </div>
                {l.description && (
                  <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{l.description}</p>
                )}
                <div className="mt-2 text-[10px] text-muted-foreground">
                  Updated {new Date(l.updated_at).toLocaleDateString()} · {l.total_rows} total rows
                </div>
              </button>
            ))
          )}
        </aside>

        {/* Right pane: selected list */}
        <section className="col-span-12 md:col-span-8 lg:col-span-9">
          {selected ? (
            <ListView
              list={selected}
              onChanged={fetchLists}
              onDeleteRequested={() => setConfirmDeleteList(selected)}
            />
          ) : (
            <div className="flex h-64 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground">
              {lists.length > 0 ? "Pick a list on the left." : "Create a list to get started."}
            </div>
          )}
        </section>
      </div>

      <CreateListDialog
        open={creatingOpen}
        onClose={() => setCreatingOpen(false)}
        onCreated={(l) => {
          fetchLists();
          setSelectedId(l.id);
        }}
      />

      <AlertDialog open={!!confirmDeleteList} onOpenChange={(o) => !o && setConfirmDeleteList(null)}>
        <AlertDialogContent data-testid="contacts-delete-list-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete list?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes <b>{confirmDeleteList?.name}</b> and <b>all {confirmDeleteList?.valid_rows} contacts</b> in it. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="contacts-delete-list-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                try {
                  await api.delete(`/contact-lists/${confirmDeleteList.id}`);
                  toast.success("List deleted");
                  setSelectedId(null);
                  setConfirmDeleteList(null);
                  fetchLists();
                } catch (err) {
                  toastError(err, "Delete failed");
                }
              }}
            >
              Delete list
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateListDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="contacts-create-dialog">
        <DialogHeader>
          <DialogTitle>New contact list</DialogTitle>
          <DialogDescription>You'll upload phones into this list next.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="lname">Name</Label>
            <Input id="lname" value={name} onChange={(e) => setName(e.target.value)} data-testid="contacts-create-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ldesc">Description (optional)</Label>
            <Textarea id="ldesc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="contacts-create-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="contacts-create-submit"
            disabled={submitting || !name.trim()}
            onClick={async () => {
              setSubmitting(true);
              try {
                const res = await api.post("/contact-lists", { name: name.trim(), description: description.trim() || null });
                toast.success("List created");
                onCreated(res.data);
                onClose();
              } catch (err) {
                toastError(err, "Failed to create list");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListView({ list, onChanged, onDeleteRequested }) {
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState({ total: 0, contacts: [] });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);
  const [confirmDeleteContact, setConfirmDeleteContact] = useState(null);
  const fileRef = useRef(null);
  const limit = 50;

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = { limit, offset: page * limit };
      if (statusFilter !== "__all__") params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get(`/contact-lists/${list.id}/contacts`, { params });
      setContacts(res.data);
    } catch (err) {
      toastError(err, "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    setLastUpload(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.id]);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.id, page, statusFilter, search]);

  const onFileChosen = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post(`/contact-lists/${list.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastUpload(res.data);
      toast.success(`Imported ${res.data.valid_rows} of ${res.data.total_rows} rows`);
      onChanged();
      fetchContacts();
    } catch (err) {
      toastError(err, "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4" data-testid="contacts-list-view">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold" data-testid="contacts-list-name">{list.name}</h2>
            {list.description && <p className="text-sm text-muted-foreground">{list.description}</p>}
            {list.source_filename && (
              <p className="mt-1 text-xs text-muted-foreground">last upload: {list.source_filename}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              className="hidden"
              data-testid="contacts-file-input"
              onChange={(e) => onFileChosen(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="contacts-upload-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload contacts
            </Button>
            <Button variant="outline" onClick={onDeleteRequested} data-testid="contacts-delete-list-btn">
              <Trash2 className="h-4 w-4 text-destructive" /> Delete list
            </Button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 text-center">
          <Stat label="Total rows" value={list.total_rows} />
          <Stat label="Valid" value={list.valid_rows} />
          <Stat label="Invalid" value={list.invalid_rows} />
          <Stat label="Duplicates" value={list.duplicate_rows} />
        </div>
      </div>

      {lastUpload && lastUpload.sample_invalid && lastUpload.sample_invalid.length > 0 && (
        <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-4 text-xs" data-testid="contacts-upload-issues">
          <div className="font-medium mb-2">
            Last upload: <b>{lastUpload.valid_rows}</b> valid · <b>{lastUpload.invalid_rows}</b> invalid · <b>{lastUpload.duplicate_rows}</b> duplicates
          </div>
          <details>
            <summary className="cursor-pointer">First 20 invalid rows</summary>
            <ul className="mt-2 space-y-0.5 max-h-48 overflow-auto">
              {lastUpload.sample_invalid.slice(0, 20).map((r, i) => (
                <li key={i} className="font-mono">
                  Row {r.row_number}: {r.phone || "<empty>"} — {r.reason}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-3">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-44" data-testid="contacts-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="called">Called</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="do_not_call">Do not call</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Search phone or name…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="max-w-xs"
            data-testid="contacts-filter-search"
          />
          <div className="ml-auto text-xs text-muted-foreground">
            {contacts.total} contacts
          </div>
        </div>
        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : contacts.total === 0 ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center" data-testid="contacts-empty-table">
            <FileSpreadsheet className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm">No contacts in this list yet. Upload a CSV / XLS / XLSX file.</p>
          </div>
        ) : (
          <Table data-testid="contacts-table">
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Custom vars</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.contacts.map((c) => {
                const cvKeys = Object.keys(c.custom_vars || {});
                return (
                  <TableRow key={c.id} data-testid={`contacts-row-${c.id}`}>
                    <TableCell className="font-mono text-sm">{formatPhone(c.phone)}</TableCell>
                    <TableCell className="text-sm">
                      {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {cvKeys.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span title={JSON.stringify(c.custom_vars)} className="font-mono">
                          {cvKeys.length} keys
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_COLORS[c.status] || "outline"}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{c.attempts}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDeleteContact(c)}
                        data-testid={`contacts-delete-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {contacts.total > limit && (
          <div className="flex items-center justify-between border-t border-border p-2 text-xs" data-testid="contacts-pagination">
            <span>Page {page + 1} of {Math.ceil(contacts.total / limit)}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline" disabled={(page + 1) * limit >= contacts.total} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmDeleteContact} onOpenChange={(o) => !o && setConfirmDeleteContact(null)}>
        <AlertDialogContent data-testid="contacts-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <b>{confirmDeleteContact?.phone}</b> from this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="contacts-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                try {
                  await api.delete(`/contacts/${confirmDeleteContact.id}`);
                  toast.success("Contact removed");
                  setConfirmDeleteContact(null);
                  onChanged();
                  fetchContacts();
                } catch (err) {
                  toastError(err, "Delete failed");
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border bg-background p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}
